import { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../config/firebase";
import { collection, query, where, getDocs } from "firebase/firestore";
import { cancelOrder, isSyncLocked } from "../services/orderService";
import { useAuth } from "../context/AuthContext";
import { getAllProducts, getCustomerRecords } from "../services/dataCache";
import { useLang } from "../i18n/LanguageContext";
import { sortOrderItems } from "../utils/orderSort";
import OrderSheetModal from "../components/OrderSheetModal";

export default function MyOrders({ onLoadCart }) {
  const { user, profile } = useAuth();
  const { t } = useLang();
  const isStaffRole = profile?.role === "rep" || profile?.role === "admin";
  // Per-customer price visibility (backlog #4) — staff always see prices.
  const showPrices = isStaffRole || profile?.showPrices !== false;
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // order id being cancelled/edited
  const [expanded, setExpanded] = useState(null);
  // Order-sheet popup for ANY past order (Brian, 2026-08-24): the same
  // list ↔ photos view the cart has, so customers can always look back at
  // exactly what they ordered.
  const [sheetOrder, setSheetOrder] = useState(null);

  function sheetRowsFor(o) {
    const storageBySku = Object.fromEntries(
      Object.entries(products).map(([sku, p]) => [sku, p.storageMethod || ""])
    );
    const toRow = (item, idx) => ({
      key: `${idx}:${item.sku}`,
      sku: item.sku,
      name: item.creditLine ? null : (products[item.sku]?.name || item.sku),
      qty: item.qty,
      imageUrl: products[item.sku]?.imageUrl || null,
      isReturn: item.qty < 0,
      creditLine: item.creditLine,
      creditDesc: item.creditDesc,
      freeGoods: item.freeGoods
    });
    const { sorted } = sortOrderItems(o.items || [], storageBySku);
    return { rows: sorted.map(toRow), originalRows: (o.items || []).map(toRow) };
  }

  // Store-based linking (2026-08-20): accounts share a store via the user
  // doc's customerRecordId. "My orders" = orders under my uid PLUS every
  // order for my store's record (placed by another manager or by staff on
  // the store's behalf), merged and deduped by order id.
  const myRecordId = profile?.customerRecordId || null;

  useEffect(() => {
    async function load() {
      // Staff (Brian, 2026-08-24): My Orders is the staff's everything-hub —
      // ALL orders, including ones customers placed themselves (the "Placed
      // for" column says whose). Customers see own + their store's orders.
      const orderQueries = isStaffRole
        ? [getDocs(collection(db, "orders"))]
        : [getDocs(query(collection(db, "orders"), where("customerId", "==", user.uid)))];
      if (!isStaffRole && myRecordId) {
        orderQueries.push(
          getDocs(query(collection(db, "orders"), where("customerRecordId", "==", myRecordId)))
        );
      }
      const [orderSnaps, cachedProducts] = await Promise.all([
        Promise.all(orderQueries),
        getAllProducts() // session cache — 0 reads on revisit
      ]);
      const byId = new Map();
      for (const snap of orderSnaps) {
        for (const d of snap.docs) byId.set(d.id, { id: d.id, ...d.data() });
      }
      const list = [...byId.values()];
      list.sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
      setOrders(list);
      // ALL products (inactive included) — names must resolve for old orders,
      // and Reorder may include currently-inactive items (they're orderable
      // as backorders now).
      setProducts(Object.fromEntries(cachedProducts.map(p => [p.sku, p])));
      if (isStaffRole) {
        // Store names for the staff-only "For" column (session cache).
        const recs = await getCustomerRecords();
        setRecordNames(Object.fromEntries(recs.map(r => [r.id, r.name])));
      }
      setLoading(false);
    }
    load();
  }, [user.uid, myRecordId, isStaffRole]);

  const [recordNames, setRecordNames] = useState({});

  // Staff "For" column: which store (or account) an order was placed for.
  function orderForLabel(o) {
    if (o.customerRecordId && recordNames[o.customerRecordId]) return recordNames[o.customerRecordId];
    if (o.customerId === user.uid) return t("myOwnAccount");
    return "—";
  }

  function formatDate(ts) {
    if (!ts || !ts.toDate) return "—";
    return ts.toDate().toLocaleString("en-CA", {
      year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit"
    });
  }

  // Per-day divider rows for STAFF (admin + rep — Brian, 2026-09-01; same
  // look as the admin Orders page). Customers keep the plain list.
  const showDayDividers = isStaffRole;
  function dayKey(ts) {
    return ts && ts.toDate ? ts.toDate().toDateString() : "unknown";
  }
  function dayLabel(ts) {
    if (!ts || !ts.toDate) return "Unknown date";
    return ts.toDate().toLocaleDateString("en-CA", {
      weekday: "long", year: "numeric", month: "short", day: "numeric"
    });
  }

  function statusLabel(s) {
    if (s === "synced") return <span className="status-synced">{t("invoiced")}</span>;
    if (s === "cancelled") return <span className="status-cancelled">{t("cancelled")}</span>;
    // pending_sync AND error both show as processing to the customer
    return <span className="status-pending">{t("processing")}</span>;
  }

  // Buttons render for pending/error orders but gray out while a QB sync
  // session holds the order (isSyncLocked) — cancelOrder also rejects locked
  // orders server-side, so a stale page can't slip a cancel through.
  const editable = (o) => o.status === "pending_sync" || o.status === "error";

  async function handleCancel(o) {
    if (!window.confirm(t("confirmCancel", o.orderNumber))) return;
    setBusy(o.id);
    try {
      await cancelOrder(o.id);
      setOrders(prev => prev.map(x => x.id === o.id ? { ...x, status: "cancelled" } : x));
    } catch (err) {
      alert(err.message);
    } finally {
      setBusy(null);
    }
  }

  // Reorder (backlog): rebuild the cart from any past order — no cancelling,
  // current product data and prices, inactive items skipped with a heads-up.
  function handleReorder(o) {
    // keepHidden items (2026-08-26) are invisible to customers everywhere —
    // reorder skips them too (counted in the "skipped" heads-up). Staff keep them.
    const available = (o.items || []).filter(item =>
      products[item.sku] && (isStaffRole || !products[item.sku].keepHidden));
    if (available.length === 0) {
      alert(t("noneAvailable"));
      return;
    }
    const skipped = (o.items || []).length - available.length;
    if (skipped > 0) {
      alert(t("someSkipped", skipped));
    }
    onLoadCart(available.map(item => ({ ...products[item.sku], qty: item.qty })));
    navigate("/cart");
  }

  async function handleEdit(o) {
    if (!window.confirm(t("confirmEdit", o.orderNumber))) return;
    setBusy(o.id);
    try {
      await cancelOrder(o.id);
      // Rebuild cart lines from the order, using current product data
      const cartItems = (o.items || [])
        // skip vanished SKUs; customers also lose keepHidden lines on edit
        .filter(item => products[item.sku] && (isStaffRole || !products[item.sku].keepHidden))
        .map(item => ({ ...products[item.sku], qty: item.qty }));
      onLoadCart(cartItems);
      navigate("/cart");
    } catch (err) {
      alert(err.message);
      setBusy(null);
    }
  }

  if (loading) return <p>{t("loadingOrders")}</p>;
  if (orders.length === 0) return <div className="my-orders"><h2>{t("myOrdersTitle")}</h2><p>{t("noOrdersYet")}</p></div>;

  return (
    <div className="my-orders">
      <h2>{t("myOrdersTitle")}</h2>
      <table>
        <thead>
          <tr>
            <th>{t("orderCol")}</th><th>{t("date")}</th>
            {isStaffRole && <th>{t("placedFor")}</th>}
            <th>{t("items")}</th>
            {showPrices && <th>{t("subtotal")}</th>}
            <th>{t("status")}</th><th></th>
          </tr>
        </thead>
        <tbody>
          {orders.map((o, i) => {
            const locked = isSyncLocked(o);
            const newDay = i === 0 || dayKey(o.createdAt) !== dayKey(orders[i - 1].createdAt);
            const cols = 5 + (isStaffRole ? 1 : 0) + (showPrices ? 1 : 0);
            return (
              <Fragment key={o.id}>
                {showDayDividers && newDay && (
                  <tr className="date-divider-row">
                    <td colSpan={cols}>{dayLabel(o.createdAt)}</td>
                  </tr>
                )}
                <tr className="clickable order-row" onClick={() => setExpanded(expanded === o.id ? null : o.id)}>
                  <td>{o.orderNumber}</td>
                  <td>{formatDate(o.createdAt)}</td>
                  {isStaffRole && <td>{orderForLabel(o)}</td>}
                  <td>{(o.items || []).length}</td>
                  {showPrices && <td>${(o.totalAmount || 0).toFixed(2)}</td>}
                  <td>{statusLabel(o.status)}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <button
                      className="mini-btn"
                      title={t("viewOrderSheet")}
                      onClick={() => setSheetOrder(o)}
                    >📋</button>
                    <button
                      className="mini-btn"
                      title={t("reorderHint")}
                      onClick={() => handleReorder(o)}
                    >{t("reorder")}</button>
                    {editable(o) && !isStaffRole && (
                      <>
                        <button
                          className="mini-btn"
                          disabled={busy === o.id || locked}
                          title={locked ? t("syncLockMsg") : undefined}
                          onClick={() => handleEdit(o)}
                        >{t("edit")}</button>
                        <button
                          className="mini-btn danger"
                          disabled={busy === o.id || locked}
                          title={locked ? t("syncLockMsg") : undefined}
                          onClick={() => handleCancel(o)}
                        >{t("cancel")}</button>
                      </>
                    )}
                    {/* Staff edit/cancel lives on the Orders page — its Edit
                        does the FAITHFUL rebuild with the store pre-selected */}
                  </td>
                </tr>
                {expanded === o.id && (
                  <tr>
                    <td colSpan={5 + (showPrices ? 1 : 0) + (isStaffRole ? 1 : 0)}>
                      {o.comment && (
                        <div className="order-comment-box">
                          💬 <strong>{t("yourNote")}</strong> {o.comment}
                        </div>
                      )}
                      <table className="order-items-table">
                        <thead>
                          <tr><th>{t("product")}</th><th>{t("qty")}</th>{showPrices && <th>{t("price")}</th>}</tr>
                        </thead>
                        <tbody>
                          {(o.items || []).map((item, idx) => (
                            <tr key={idx}>
                              <td>
                                {item.qty < 0 && <span className="return-tag">↩ </span>}
                                {item.freeGoods && <span className="free-tag">🎁 </span>}
                                {item.creditLine
                                  ? <>Credit — {item.creditDesc}</>
                                  : (products[item.sku]?.name || item.sku)}
                                <span className="item-sku-inline">#{item.sku}</span>
                              </td>
                              <td>{item.qty}</td>
                              {showPrices && <td>${item.priceAtOrder.toFixed(2)}</td>}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      <p className="inventory-help-note">
        {t("ordersHelpNote")}
      </p>
      {sheetOrder && (
        <OrderSheetModal
          title={sheetOrder.orderNumber}
          customerName={null}
          dateLabel={formatDate(sheetOrder.createdAt)}
          {...sheetRowsFor(sheetOrder)}
          onClose={() => setSheetOrder(null)}
        />
      )}
    </div>
  );
}
